/**
 * Admin captains list page mounted at `/admin`.
 *
 * Loads page `n` of captains from `ApiClient.listCaptains({page, limit: 20})`
 * and renders a table with columns: thumbnail, name, jobTitle, phoneNumber,
 * and actions (تعديل / حذف / عرض QR). Displays:
 *
 *  - a centered spinner while the initial / next-page request is pending
 *    (Req 8.1: page loads in ≤ 3s under normal network),
 *  - a localized error message + "إعادة المحاولة" button on failure,
 *  - an empty-state message "لا يوجد كباتن مسجلون" with an "إضافة كابتن"
 *    button when the list is empty (Req 8.4),
 *  - prev/next pagination controls disabled at the boundaries; the total
 *    number of pages is `Math.ceil(total / limit)` (Req 8.3).
 *
 * Delete flow (Req 7.4, 8.6):
 *  - Clicking "حذف" opens `<DeleteCaptainDialog>`.
 *  - `onConfirm` calls `ApiClient.deleteCaptain`. On success the row is
 *    filtered out of the local list and `total` is decremented.
 *  - On failure the dialog itself surfaces the inline error message; the
 *    list page does **not** remove the row from the displayed list.
 *
 * QR flow (Req 8.5):
 *  - Clicking "عرض QR" opens an inline modal that fetches the PNG blob via
 *    `ApiClient.getQrPng` and renders it with a download link. The modal is
 *    intentionally minimal here so this task can land independently of the
 *    sibling `AdminQrModal` work item; the modal can be replaced with that
 *    component in a follow-up without changing the page's contract.
 *
 * Validates: Requirements 8.1, 8.3, 8.4 (and integrates 7.4, 8.5, 8.6)
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiClient, type Captain } from '../api/ApiClient';
import { DeleteCaptainDialog } from './DeleteCaptainDialog';

import styles from './AdminCaptainsListPage.module.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Page size mandated by Req 8.3. */
const PAGE_LIMIT = 20;

const EMPTY_MESSAGE = 'لا يوجد موظفون مسجلون في هذا القسم';
const ERROR_MESSAGE = 'تعذّر تحميل قائمة الموظفين';
const DELETE_FAILURE_MESSAGE = 'تعذّر حذف الموظف، يرجى المحاولة لاحقاً';
const QR_FAILURE_MESSAGE = 'تعذّر توليد رمز QR، يرجى المحاولة لاحقاً';

// Inline placeholder for missing thumbnails so the table never breaks layout.
const PLACEHOLDER_THUMBNAIL =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>" +
      "<rect width='64' height='64' fill='#e9ecef'/>" +
      "<circle cx='32' cy='26' r='12' fill='#adb5bd'/>" +
      "<path d='M12 60c0-11 9-20 20-20s20 9 20 20' fill='#adb5bd'/>" +
      '</svg>',
  );

type Status = 'loading' | 'success' | 'error';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminCaptainsListPage(): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [items, setItems] = useState<Captain[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Department filter state
  const [activeDepartment, setActiveDepartment] = useState<string>('');
  const [allDepartments, setAllDepartments] = useState<string[]>([]);

  // Reload-trigger counter used by the "إعادة المحاولة" button.
  const [reloadToken, setReloadToken] = useState(0);

  // Delete dialog state. `target` is set when the admin clicks "حذف"; the
  // dialog is rendered as long as `target !== null`.
  const [deleteTarget, setDeleteTarget] = useState<Captain | null>(null);
  // Persistent error banner shown above the table when a delete fails.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // QR modal state.
  const [qrTarget, setQrTarget] = useState<Captain | null>(null);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setErrorMessage(null);

    ApiClient.listCaptains({ page, limit: PAGE_LIMIT, department: activeDepartment || undefined })
      .then((result) => {
        if (cancelled) return;
        setItems(Array.isArray(result.items) ? result.items : []);
        setTotal(typeof result.total === 'number' ? result.total : 0);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error && err.message.length > 0
            ? err.message
            : ERROR_MESSAGE;
        setErrorMessage(message);
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [page, reloadToken, activeDepartment]);

  // Fetch all departments once for the filter bar
  useEffect(() => {
    ApiClient.listCaptains({ page: 1, limit: 50 })
      .then((result) => {
        const depts = Array.from(
          new Set(
            result.items
              .map((c) => c.department ?? '')
              .filter((d) => d !== '')
          )
        ).sort();
        setAllDepartments(depts);
      })
      .catch(() => { /* silent */ });
  }, [reloadToken]);

  const handleRetry = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  // -------------------------------------------------------------------------
  // Pagination helpers
  // -------------------------------------------------------------------------

  const totalPages = total > 0 ? Math.ceil(total / PAGE_LIMIT) : 1;
  const canGoPrev = page > 1;
  const canGoNext = page < totalPages;

  const handlePrev = useCallback(() => {
    setPage((current) => (current > 1 ? current - 1 : current));
  }, []);

  const handleNext = useCallback(() => {
    setPage((current) => current + 1);
  }, []);

  const handleDepartmentChange = useCallback((dept: string) => {
    setActiveDepartment(dept);
    setPage(1);
  }, []);

  // -------------------------------------------------------------------------
  // Delete flow
  // -------------------------------------------------------------------------

  const handleDeleteClick = useCallback((captain: Captain) => {
    setDeleteError(null);
    setDeleteTarget(captain);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleDeleteConfirm = useCallback(
    async (id: string): Promise<void> => {
      try {
        await ApiClient.deleteCaptain(id);
        // On success: filter the row out locally and adjust the total.
        // (Req 8.6: only remove on success.)
        setItems((prev) => prev.filter((c) => c._id !== id));
        setTotal((prev) => (prev > 0 ? prev - 1 : 0));
        setDeleteError(null);
        setDeleteTarget(null);
      } catch (err: unknown) {
        // Re-throw so the dialog displays its inline error and stays open.
        // Also surface a top-level banner so the admin sees the failure
        // even after dismissing the dialog. (Req 8.6 forbids removing the
        // captain from the displayed list on failure.)
        setDeleteError(DELETE_FAILURE_MESSAGE);
        throw err instanceof Error ? err : new Error(DELETE_FAILURE_MESSAGE);
      }
    },
    [],
  );

  // -------------------------------------------------------------------------
  // QR flow
  // -------------------------------------------------------------------------

  const handleQrClick = useCallback((captain: Captain) => {
    setQrTarget(captain);
  }, []);

  const handleQrClose = useCallback(() => {
    setQrTarget(null);
  }, []);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className={styles.page} data-testid="admin-captains-list-page" dir="rtl">
      <header className={styles.header}>
        <h1 className={styles.title}>إدارة الموظفين</h1>
        <Link
          to="/admin/captains/new"
          className={styles.addButton}
          data-testid="add-captain-link"
        >
          إضافة موظف
        </Link>
      </header>

      {/* Department filter bar */}
      {allDepartments.length > 0 && (
        <div className={styles.filterBar} dir="rtl" data-testid="department-filter">
          <button
            type="button"
            className={activeDepartment === '' ? styles.filterActive : styles.filterBtn}
            onClick={() => handleDepartmentChange('')}
          >
            الكل
          </button>
          {allDepartments.map((dept) => (
            <button
              key={dept}
              type="button"
              className={activeDepartment === dept ? styles.filterActive : styles.filterBtn}
              onClick={() => handleDepartmentChange(dept)}
            >
              {dept}
            </button>
          ))}
        </div>
      )}

      {deleteError !== null && (
        <div className={styles.banner} role="alert" data-testid="delete-error-banner">
          {deleteError}
        </div>
      )}

      {status === 'loading' && (
        <div className={styles.center} data-testid="captains-loading">
          <div className={styles.loadingBox} role="status" aria-live="polite">
            <div className={styles.spinner} aria-hidden="true" />
            <p className={styles.loadingText}>جاري التحميل...</p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className={styles.center} data-testid="captains-error">
          <div className={styles.errorBox} role="alert">
            <p>{errorMessage ?? ERROR_MESSAGE}</p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={handleRetry}
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      )}

      {status === 'success' && items.length === 0 && (
        <div className={styles.center} data-testid="captains-empty">
          <p className={styles.emptyMessage}>{EMPTY_MESSAGE}</p>
          <Link
            to="/admin/captains/new"
            className={styles.addButton}
            data-testid="empty-add-captain-link"
          >
            إضافة موظف
          </Link>
        </div>
      )}

      {status === 'success' && items.length > 0 && (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table} data-testid="captains-table">
              <thead>
                <tr>
                  <th scope="col">الصورة</th>
                  <th scope="col">الاسم</th>
                  <th scope="col">المسمى الوظيفي</th>
                  <th scope="col">القسم</th>
                  <th scope="col">رقم الهاتف</th>
                  <th scope="col">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {items.map((captain) => (
                  <tr key={captain._id} data-testid={`captain-row-${captain._id}`}>
                    <td>
                      <img
                        className={styles.thumbnail}
                        src={
                          captain.imageUrl && captain.imageUrl.length > 0
                            ? captain.imageUrl
                            : PLACEHOLDER_THUMBNAIL
                        }
                        alt={captain.name}
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src =
                            PLACEHOLDER_THUMBNAIL;
                        }}
                      />
                    </td>
                    <td>{captain.name}</td>
                    <td>{captain.jobTitle}</td>
                    <td>
                      {captain.department && captain.department !== '' ? (
                        <span className={styles.deptBadge}>{captain.department}</span>
                      ) : (
                        <span className={styles.deptEmpty}>—</span>
                      )}
                    </td>
                    <td dir="ltr" className={styles.phone}>
                      {captain.phoneNumber}
                    </td>
                    <td className={styles.actions}>
                      <Link
                        to={`/admin/captains/${captain._id}/edit`}
                        className={styles.actionLink}
                        data-testid={`edit-${captain._id}`}
                      >
                        تعديل
                      </Link>
                      <button
                        type="button"
                        className={styles.deleteButton}
                        onClick={() => handleDeleteClick(captain)}
                        data-testid={`delete-${captain._id}`}
                      >
                        حذف
                      </button>
                      <button
                        type="button"
                        className={styles.qrButton}
                        onClick={() => handleQrClick(captain)}
                        data-testid={`qr-${captain._id}`}
                      >
                        عرض QR
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <nav
            className={styles.pagination}
            aria-label="ترقيم الصفحات"
            data-testid="pagination"
          >
            <button
              type="button"
              onClick={handlePrev}
              disabled={!canGoPrev}
              className={styles.paginationButton}
              data-testid="pagination-prev"
            >
              السابق
            </button>
            <span className={styles.paginationStatus} data-testid="pagination-status">
              صفحة {page} من {totalPages}
            </span>
            <button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext}
              className={styles.paginationButton}
              data-testid="pagination-next"
            >
              التالي
            </button>
          </nav>
        </>
      )}

      <DeleteCaptainDialog
        open={deleteTarget !== null}
        captain={
          deleteTarget !== null
            ? { _id: deleteTarget._id, name: deleteTarget.name }
            : null
        }
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {qrTarget !== null && (
        <QrModal captain={qrTarget} onClose={handleQrClose} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline minimal QR modal
//
// A self-contained QR viewer used until the dedicated `AdminQrModal`
// component lands as part of task 12.5. Loads the QR PNG via
// `ApiClient.getQrPng`, renders it as an `<img>`, and offers a download link
// that saves the file as `captain-{id}.png` (Req 9.4).
// ---------------------------------------------------------------------------

interface QrModalProps {
  captain: Captain;
  onClose: () => void;
}

function QrModal({ captain, onClose }: QrModalProps): JSX.Element {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [errorMessageText, setErrorMessageText] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    setImageSrc(null);
    setErrorMessageText(null);

    ApiClient.getQrPng(captain._id)
      .then((blob) => {
        if (cancelled) return;
        if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
          createdUrl = URL.createObjectURL(blob);
          setImageSrc(createdUrl);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setErrorMessageText(QR_FAILURE_MESSAGE);
      });

    return () => {
      cancelled = true;
      if (
        createdUrl !== null &&
        typeof URL !== 'undefined' &&
        typeof URL.revokeObjectURL === 'function'
      ) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [captain._id]);

  const downloadName = `captain-${captain._id}.png`;

  return (
    <div className={styles.qrOverlay} data-testid="qr-modal-overlay">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`رمز QR للكابتن ${captain.name}`}
        className={styles.qrCard}
      >
        <h2 className={styles.qrTitle}>رمز QR — {captain.name}</h2>
        {errorMessageText !== null ? (
          <p className={styles.qrError} role="alert">
            {errorMessageText}
          </p>
        ) : imageSrc !== null ? (
          <img
            src={imageSrc}
            alt={`QR ${captain.name}`}
            className={styles.qrImage}
            data-testid="qr-image"
          />
        ) : (
          <div className={styles.spinner} aria-hidden="true" />
        )}
        <div className={styles.qrActions}>
          {imageSrc !== null && errorMessageText === null && (
            <a
              href={imageSrc}
              download={downloadName}
              className={styles.qrDownload}
              data-testid="qr-download"
            >
              تنزيل
            </a>
          )}
          <button
            type="button"
            className={styles.qrClose}
            onClick={onClose}
            data-testid="qr-close"
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
}
